import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import Swiper from 'swiper';

import { SelectModalListItem } from '@/components/molecules/modal/select-modal';
import { useCompleteMissionMutation } from '@/hooks/mission/mission.gql.impl';
import { UserMissionTypeVo } from '@/hooks/mission/types';
import { useBroadcastChannel } from '@/hooks/mission/useBroadcastChannel';
import useMissionTimer from '@/hooks/mission/useMissionTimer';
import useAuthModal from '@/hooks/useAuthModal';
import useCustomRouter from '@/hooks/useCustomRouter';
import useModal from '@/hooks/useModal';
import useReportModal, { ReportData } from '@/hooks/useReportModal';
import useScreenFocus from '@/hooks/useScreenFocus';
import { tokenState } from '@/recoil/atoms/common/auth';
import { userState } from '@/recoil/atoms/common/user';
import { User } from '@/types/Auth';
import { QuestionAccessibleType } from '@/types/bff/gql-schema';
import { Nullable, ROUTES } from '@/types/common';
import { AttachmentBlindType } from '@/types/community';
import { MODAL_BUTTON_TYPE, MODAL_TYPE } from '@/types/modal';
import { AnswerLikeType, AnswererType, QnaAttachment, QnaDetailVo, RelatedQnaVo } from '@/types/qna';
import { getPageName, mobileLogger } from '@/utils/logger';
import { NATIVE_POST_MESSAGE_TYPE, historyBack, isWebView, sendPostMessageToNative } from '@/utils/mobile/RNPlugins';

import { EPixelEventName } from '@/constants/pixel';
import { ECategoryType } from '@/services/doctor/DiagnosisCategoryService';
import { useToast } from 'doctornow-design-system';

import Pixel from '@/utils/pixel/pixel';
import useAppDownloadCujModal from '../cuj/useAppDownloadCujModal';

import { DeeplinkLoggerEventName } from '@/constants/logs/deeplink';
import useAdBanner from '@/hooks/advertisement/useAdBanner';
import useNavigate from '@/hooks/useNavigate';
import { AdPageType, BannerAdExchangeModel } from '@/models/ad/list';
import { EQuestionCategoryId, QuestionCategoryType, questionCategoryMap } from '@/shared/constants';
import { useAirbridgeDynamicLink } from '@/shared/hooks';
import { Client } from '@/types/app';
import useCanProgressQnaWrite from './mobile/write/useCanProgressQnaWrite';
import useGetQnaDetailWithNearSimplyQuestions from './useGetQnaDetailWithNearSimplyQuestions';
import useMakeQuestionPrivate from './useMakeQuestionPrivate';
import useGetRelatedQna from './useRelatedQna';
import useSetAnswerDeleteLike from './useSetAnswerDeleteLike';
import useSetAnswerLike from './useSetAnswerLike';
import useSetQuestionDisLike from './useSetQuestionDisLike';
import useSetQuestionLike from './useSetQuestionLike';

interface QuestionDetailViewModel {
  qnaDetail?: QnaDetailVo;
  isQnaDetailLoading: boolean;
  relatedQna?: RelatedQnaVo[];
  isRelatedQnaLoading: boolean;
  qnaAttachments: QnaDetailVo['attachments'];
  imageZoomIndex: number;
  pageName: string;
  onShareModal: boolean;
  user: User;
  showReportModal: boolean;
  reportItems: SelectModalListItem[];
  imageSlideIdx: number;
  isNotMine: boolean;
  isPrivateQuestion: boolean;
  timerVisible: boolean;
  displayTime: string;
  missionTargetTime: number;
  missionCompleted: boolean;
  questionBannerInfo: QuestionBannerInfoValueType;
  isShowPrivateModal: boolean;
  isMyQuestion: boolean;
  getStickyButtonText: () => string;
  shouldShowEventText: () => boolean;
  onShowModal: (e: React.MouseEvent, data: QuestionDetailModalDataType) => void;
  onVisibleDimmedImage: (index: number) => void;
  onInValidateDetailQuery: () => void;
  onClickImage: (index: number) => void;
  onCloseImageZoom: () => void;
  onClickLikeQuestion: (like: boolean) => void;
  onChangeShowShareModal: () => void;
  onClickLikeAnswer: (answerPid: string, type: AnswerLikeType, myLikeType: Nullable<AnswerLikeType>) => void;
  onClickMagazine: (magazinePid: string) => void;
  moveToDoctorDetailPage: (answererType: AnswererType, doctorId?: string | null) => void;
  onClickRelatedQna: (qna: RelatedQnaVo) => void;
  onCloseReportModal: () => void;
  onChangeImageBlindType: (index: number) => void;
  onChangeSlideImage: (swiper: Swiper) => void;
  onClickNearQna: (direction: string, questionPid?: string) => void;
  moveToHospitalDetail: (hospitalPid: string) => void;
  goBack: () => void;
  onClickWriteReview: (answerPid: string) => void;
  onClickStickButton: () => void;
  onClickBanner: () => void;
  toggleIsShowPrivateModal: () => void;
  onClickQuestionPrivate: () => void;
  targetQuestionCategory: QuestionCategoryType;
  onClickTopCta: () => void;
  onClickOuterLink: (url: string) => void;
  extractTextFromChildren: (children: React.ReactNode) => string;
  bannerAdExchange: BannerAdExchangeModel | null;
  handleSendExposureLog: () => void;
  subCategoryName?: string;
  isEnabledWebDiagnosisCategory: boolean;
  hasSelectedRelatedQnaPid: boolean;
  onNavigateToTelemedicine: () => void;
  onNavigateToNextQna: () => void;
  onCloseRelatedQnaBottomSheet: () => void;
}

const DIET_QUESTION_CATEGORY_ID = '24';

const questionBannerInfoId = {
  Default: 'default',
  Skin: 'skin',
  LowCost: 'lowcost',
  Awkward: 'awkward',
  Fast: 'fast',
  Baby: 'baby',
} as const;

type QuestionBannerInfoIdType = (typeof questionBannerInfoId)[keyof typeof questionBannerInfoId];

export type QuestionBannerInfoValueType = {
  headText: string;
  mainTextRows: string[];
  buttonText: string;
};

const questionBannerInfo: Record<QuestionBannerInfoIdType, QuestionBannerInfoValueType> = {
  default: {
    headText: '진료가 필요한 상황인가요?',
    mainTextRows: ['닥터나우는', '24시간 열려있어요!'],
    buttonText: '비대면 진료 받으러 가기',
  },
  skin: {
    headText: '피부 고민, 빠르게 해결하고 싶다면?',
    mainTextRows: ['진료받고 딱 맞는 약을', '최저가로 처방 받으세요!'],
    buttonText: '최저가로 비대면 진료받기',
  },
  lowcost: {
    headText: '더 싸고 가까운 병원, 약국 없나?',
    mainTextRows: ['최저가로 진료부터', '약 처방까지 가능해요!'],
    buttonText: '최저가로 비대면 진료받기',
  },
  awkward: {
    headText: '병원 가기 민망하다면?',
    mainTextRows: ['병원 방문 없이', '전화 진료 받아 보세요!'],
    buttonText: '마음 편히 진료받기',
  },
  fast: {
    headText: '빠르게 낫고 싶다면?',
    mainTextRows: ['집에서 5분 만에', '진료받을 수 있어요!'],
    buttonText: '지금 비대면 진료 받기',
  },
  baby: {
    headText: '지금 아이가 아프다면?',
    mainTextRows: ['닥터나우는 늦은 밤,', '주말에도 진료 가능해요!'],
    buttonText: '대기 없이 진료받기',
  },
};

const categoryToBannerMap: Partial<Record<EQuestionCategoryId, QuestionBannerInfoIdType>> = {
  [EQuestionCategoryId.INTERNAL_MEDICINE]: questionBannerInfoId.Fast,
  [EQuestionCategoryId.UROLOGY]: questionBannerInfoId.Awkward,
  [EQuestionCategoryId.OBSTETRICS_AND_GYNECOLOGY]: questionBannerInfoId.Awkward,
  [EQuestionCategoryId.PEDIATRICS]: questionBannerInfoId.Baby,
  [EQuestionCategoryId.OTORHINOLARYNGOLOGY]: questionBannerInfoId.Fast,
  [EQuestionCategoryId.DERMATOLOGY]: questionBannerInfoId.Skin,
  [EQuestionCategoryId.COLD_FLU]: questionBannerInfoId.Fast,
  [EQuestionCategoryId.MENS_HEALTH]: questionBannerInfoId.Awkward,
  [EQuestionCategoryId.DIET]: questionBannerInfoId.LowCost,
  [EQuestionCategoryId.WOMENS_HEALTH]: questionBannerInfoId.Awkward,
  [EQuestionCategoryId.DIGESTIVE_DISEASE]: questionBannerInfoId.Fast,
  [EQuestionCategoryId.ACNE_SKIN_DISEASE]: questionBannerInfoId.Skin,
  [EQuestionCategoryId.CORONA_TREATMENT]: questionBannerInfoId.Fast,
  [EQuestionCategoryId.HAIR_LOSS]: questionBannerInfoId.LowCost,
  [EQuestionCategoryId.PAIN]: questionBannerInfoId.Fast,
};

const getBannerInfoByCategory = (id: string) => {
  const categoryId = id as EQuestionCategoryId;
  const bannerId = categoryToBannerMap[categoryId] ?? questionBannerInfoId.Default;
  return questionBannerInfo[bannerId];
};

const getCategoryInfo = (id: string) => {
  const categoryId = id as EQuestionCategoryId;
  return questionCategoryMap[categoryId];
};

export type QuestionDetailModalDataType = { report?: ReportData; private?: any };

const useQuestionDetailViewModel = (client: Client): QuestionDetailViewModel => {
  const isFocused = useScreenFocus();
  const queryClient = useQueryClient();
  const { setShowAuthModal } = useAuthModal();
  const token = useRecoilValue(tokenState);
  const user = useRecoilValue(userState);
  const { router, moveToPage } = useCustomRouter();
  const questionPid = router.query?.id as string;
  const missionId = router.query?.missionId as string | undefined;
  const pageName = getPageName(router);
  const [imageZoomIndex, setImageZoomIndex] = useState<number>(-1);
  const [onShareModal, setOnShareModal] = useState<boolean>(false);
  const [imageSlideIdx, setImageSlideIdx] = useState(0);
  const [slideImages, setSlideImages] = useState<Array<QnaAttachment>>();
  const [isShowPrivateModal, setIsShowPrivateModal] = useState<boolean>(false);
  const [selectedRelatedQna, setSelectedRelatedQna] = useState<RelatedQnaVo | null>(null);

  const broadCastChannel = useBroadcastChannel<UserMissionTypeVo>('user-missions');
  const navigate = useNavigate();
  const airbridgeDynamicLink = useAirbridgeDynamicLink();

  const toggleIsShowPrivateModal = () => {
    setIsShowPrivateModal((currentValue) => !currentValue);
  };

  const { bannerAdExchange, handleSendExposureLog } = useAdBanner(AdPageType.QnaDetailAnswerBottomBanner, client);
  const { showModal, closeModal } = useModal();
  const { clickProgressWrite } = useCanProgressQnaWrite();
  const completeMissionMutation = useCompleteMissionMutation();
  const MISSION_TARGET_TIME = 10;
  const { displayTime, missionCompleted } = useMissionTimer({
    activated: Boolean(missionId),
    targetTime: MISSION_TARGET_TIME,
  });
  const { showReportModal, onShowReportModal, onCloseReportModal, reportItems } = useReportModal();

  // QNA 상세
  const {
    qnaDetail,
    isLoading: isQnaDetailLoading,
    refetchQnaDetail,
  } = useGetQnaDetailWithNearSimplyQuestions(questionPid);

  const [qnaAttachments, setQnaAttachments] = useState<QnaDetailVo['attachments']>(qnaDetail?.attachments ?? []);

  // 관련 질문 in QNA 상세
  const { relatedQna, isLoading: isRelatedQnaLoading } = useGetRelatedQna(questionPid);

  // 질문 좋아요 설정
  const { setLikeQuestionMutate } = useSetQuestionLike({
    onSuccess: () => onInValidateDetailQuery(),
  });

  // 질문 좋아요 해제
  const { setDisLikeQuestionMutate } = useSetQuestionDisLike({
    onSuccess: () => onInValidateDetailQuery(),
  });

  // 답변 좋아요 설정 : LIKE / DISLIKE 를 통해 도움이됬어요, 아쉬워요 구분
  const { setAnswerLikeMutate } = useSetAnswerLike({
    onSuccess: () => onInValidateDetailQuery(),
  });

  // 답변 좋아요 해제
  const { setAnswerDeleteLikeMutate } = useSetAnswerDeleteLike({
    onSuccess: () => onInValidateDetailQuery(),
  });

  // 질문 정보 저장
  const { setIsCujModalTrackingInfo } = useAppDownloadCujModal();

  // 질문 비공개
  const { makeQuestionPrivate } = useMakeQuestionPrivate();

  const { toast } = useToast();

  const subCategoryName = `QNA@${qnaDetail?.category?.type}@${qnaDetail?.category.questionCategoryId}`;

  const onClickQuestionPrivate = async () => {
    try {
      await makeQuestionPrivate(questionPid);
      refetchQnaDetail();
      toast({
        text: '비공개로 전환됐어요.',
        direction: 'bottom',
      });
      toggleIsShowPrivateModal();
    } catch (_error) {
      toast({
        text: '비공개로 전환에 실패했어요.',
        direction: 'bottom',
      });
    }
  };

  const isTreatmentCategory = qnaDetail?.category.type === ECategoryType.Treatment;
  const isDepartmentCategory = qnaDetail?.category.type === ECategoryType.Department;

  useEffect(() => {
    if (qnaDetail) {
      Pixel.trackCustom(EPixelEventName.PageIn, {
        ...(isTreatmentCategory && { symptom: qnaDetail.category.name }),
        ...(isDepartmentCategory && { subject: qnaDetail.category.name }),
      });

      setIsCujModalTrackingInfo({
        campaignName: 'web_content_qna',
        trackContent: 'web_mid_mdl_details',
        contentId: qnaDetail.questionPid,
        contentTitle: qnaDetail.title,
        contentCategory: qnaDetail.category.name,
        contentSubCategory: subCategoryName,
      });
    }
  }, [qnaDetail]);

  const isMyQuestion = qnaDetail?.user.userId === String(user?.userId);

  const questionBannerInfo = getBannerInfoByCategory(qnaDetail?.category.questionCategoryId ?? '1');

  const targetQuestionCategory = getCategoryInfo(qnaDetail?.category.questionCategoryId ?? '0');

  const getStickyButtonText = () => {
    if (isWebView()) {
      return '실시간 의료상담 물어보기';
    }

    if (targetQuestionCategory.categoryCode === 'POST_CONTRACEPTION') {
      return '빠르게 사후피임약 처방받기';
    }

    if (targetQuestionCategory.isRecommendedTreatment) {
      return `${qnaDetail?.category.name} 진료 바로 받기`;
    }

    return '지금 바로 의료 상담받기';
  };

  const shouldShowEventText = () => !isWebView();

  const onClickStickButton = () => {
    Pixel.trackCustom(EPixelEventName.BottomCtaClick, {
      ...(isTreatmentCategory && { symptom: qnaDetail.category.name }),
      ...(isDepartmentCategory && { subject: qnaDetail.category.name }),
    });

    if (targetQuestionCategory.categoryCode === 'POST_CONTRACEPTION') {
      airbridgeDynamicLink.navigateWithTracking(DeeplinkLoggerEventName.ClickCtaButton, {
        campaign: 'web_content_qna',
        content: 'web_content_flt_details',
        term: getStickyButtonText(),
        sub_id: questionPid,
        sub_id_1: qnaDetail?.title,
        sub_id_2: qnaDetail?.category.name,
      });
      return;
    }
    const shouldNavigateToWebDiagnosis = targetQuestionCategory.isRecommendedTreatment && !isWebView();
    if (shouldNavigateToWebDiagnosis) {
      navigate.navigateToTelemedicineCategoriesScreen();
      return;
    }
    clickProgressWrite();
  };

  const onClickBanner = () => {
    airbridgeDynamicLink.navigateWithTracking(DeeplinkLoggerEventName.ClickCtaButton, {
      campaign: 'web_content_qna',
      content: 'web_content_bnr_details',
      term: questionBannerInfo.buttonText,
      sub_id: questionPid,
      sub_id_1: qnaDetail?.title,
      sub_id_2: qnaDetail?.category.name,
      sub_id_3: subCategoryName,
    });
  };

  const onClickTopCta = () => {
    Pixel.trackCustom(EPixelEventName.TopCtaClick, {
      ...(isTreatmentCategory && { symptom: qnaDetail.category.name }),
      ...(isDepartmentCategory && { subject: qnaDetail.category.name }),
    });
  };

  // 질문 신고하기
  const onShowModal = (e: React.MouseEvent, data: QuestionDetailModalDataType) => {
    e.stopPropagation();

    if (!token) {
      // 로그인 정보가 없는 경우
      setShowAuthModal(true);
      return;
    }

    if (data.report) {
      onShowReportModal(data.report);
    }

    if (data.private) {
      toggleIsShowPrivateModal();
    }
  };

  // 이미지 dimmed 해제
  const onVisibleDimmedImage = useCallback(
    (index: number) => {
      const attachments = qnaAttachments.slice();
      if (attachments) {
        attachments[index as number].blindType = AttachmentBlindType.None;
        setQnaAttachments(attachments);
      }
    },
    [qnaAttachments]
  );

  const onInValidateDetailQuery = () => {
    queryClient.invalidateQueries();
  };

  // 질문에 첨부된 이미지 클릭 (dimmed 제거 / 확대)
  const onClickImage = (index: number) => {
    if (!qnaDetail?.isMine) {
      if (qnaAttachments[index as number].blindType === AttachmentBlindType.Blinded) {
        return;
      }
      if (qnaAttachments[index as number].blindType === AttachmentBlindType.Dimmed) {
        return onVisibleDimmedImage(index);
      }
    }
    setImageZoomIndex(index);
  };

  // 이미지 확대 모달 닫기
  const onCloseImageZoom = () => {
    setImageZoomIndex(-1);
  };

  // 관심질문 설정
  const onClickLikeQuestion = (like: boolean) => {
    if (!qnaDetail) return;
    if (!token) {
      setShowAuthModal(true);
    } else {
      if (like) {
        mobileLogger({
          logger: {
            eventName: 'quick_answer_question_like',
            pageName,
            payload: {
              questionId: qnaDetail.questionPid,
            },
          },
        });
        setLikeQuestionMutate({
          questionPid: qnaDetail.questionPid,
        });
      } else {
        mobileLogger({
          logger: {
            eventName: 'quick_answer_question_dislike',
            pageName,
            payload: {
              questionId: qnaDetail.questionPid,
            },
          },
        });
        setDisLikeQuestionMutate({
          questionPid: qnaDetail.questionPid,
        });
      }
    }
  };

  // 공유하기 모달 on/off
  const onChangeShowShareModal = () => {
    setOnShareModal(!onShareModal);
  };

  // 답변 도움이 됬어요 설정
  const onClickLikeAnswer = (answerPid: string, type: AnswerLikeType, myLikeType: Nullable<AnswerLikeType>) => {
    if (!token) {
      setShowAuthModal(true);
      return;
    }

    // 현재 상태가 null이 아니고 Like | DisLike 둘 중 하나일경우
    // 이미 선택해둔 값과 다시 클릭한 값이 같을 경우 취소
    if (myLikeType && type === myLikeType) {
      setAnswerDeleteLikeMutate({
        answerPid,
      });
      return;
    }

    setAnswerLikeMutate({
      request: {
        answerPid,
        type,
      },
    });
  };

  // 의료진이 추천한 콘텐츠
  const onClickMagazine = async (magazinePid: string) => {
    mobileLogger({
      logger: {
        eventName: 'recommended_magazine_click',
        pageName,
        payload: {
          id: magazinePid,
        },
      },
    });

    moveToPage({
      webLink: `${ROUTES.MAGAZINE.DETAIL.path}/${magazinePid}`,
      mobileScreenName: ROUTES.MAGAZINE.DETAIL.appScreenName,
      params: { id: magazinePid },
    });
  };

  // 의사 상세페이지 / 리스트로 이동
  const moveToDoctorDetailPage = (answererType: AnswererType, doctorId?: string | null) => {
    if (isWebView()) {
      mobileLogger({
        logger: {
          eventName: 'move_to_doctor_detail_page_click',
          pageName,
          payload: {
            doctorId: doctorId ?? '',
          },
        },
      });
      if (answererType === AnswererType.Doctor && doctorId) {
        sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.MOVE_TO_DOCTOR_DETAIL_PAGE_AFTER_DIAGNOSIS_TARGET_CHECK, {
          doctorId,
        });
      }
    }
  };

  // 관련 질문
  const onClickRelatedQna = (qna: RelatedQnaVo) => {
    mobileLogger({
      logger: {
        eventName: 'related_qna_item_click',
        pageName,
        payload: {
          section_name: pageName,
          questionId: qna.question.questionPid,
        },
      },
    });
    setSelectedRelatedQna(qna);
  };

  const onNavigateToTelemedicine = () => {
    if (selectedRelatedQna?.question.category.questionCategoryId === DIET_QUESTION_CATEGORY_ID) {
      airbridgeDynamicLink.navigateWithTracking(DeeplinkLoggerEventName.ClickCtaButton, {
        campaign: 'web_content_qna',
        content: 'web_content_mdl_details',
        term: '바로 진료 받기',
      });
      return;
    }
    navigate.navigateToTelemedicineCategoriesScreen();
  };

  const onNavigateToNextQna = () => {
    if (!selectedRelatedQna) {
      return;
    }
    navigate.navigateToQnaDetail({ questionPid: selectedRelatedQna?.question.questionPid, missionId: null });
  };

  const onCloseRelatedQnaBottomSheet = () => {
    setSelectedRelatedQna(null);
  };

  const onChangeImageBlindType = (index: number) => {
    if (slideImages) {
      if (slideImages[index as number].blindType === AttachmentBlindType.Dimmed) {
        const images = slideImages.slice();
        if (images) {
          images[index as number].blindType = AttachmentBlindType.None;
          setSlideImages(images);
        }
      }
    }
  };

  const onChangeSlideImage = (swiper: Swiper) => {
    setImageSlideIdx(swiper.realIndex);
  };

  // 이전/다음 질문
  const onClickNearQna = (direction: string, questionPid?: string) => {
    if (!questionPid) return null;
    mobileLogger({
      logger: {
        eventName: 'click_near_qna',
        pageName,
        payload: {
          section_name: pageName,
          direction,
          questionId: questionPid,
        },
      },
    });

    moveToPage({
      webLink: `${ROUTES.QNA.DETAIL.path}/${questionPid}`,
      mobileScreenName: ROUTES.QNA.DETAIL.appScreenName,
      params: { questionId: questionPid },
    });
  };

  const moveToHospitalDetail = (affiliationPid: string) => {
    moveToPage({
      webLink: `${ROUTES.HOSPITAL.DETAIL.path}${affiliationPid}`,
      mobileScreenName: ROUTES.HOSPITAL.DETAIL.appScreenName,
      params: { hospitalPid: affiliationPid },
    });
  };

  const isPrivateQuestion = useMemo(
    () => Boolean(qnaDetail && qnaDetail.accessibleType !== QuestionAccessibleType.All),
    [qnaDetail]
  );

  const isNotMine = useMemo(() => Boolean(qnaDetail && !qnaDetail.isMine), [qnaDetail]);

  const goBack = () => {
    if (missionId && !missionCompleted) {
      showModal({
        type: MODAL_TYPE.ALERT,
        props: {
          title: '아직 미션이 완료되지 않았어요',
          message: '티켓을 받으려면 미션을 완료해야해요.',
          buttons: [
            {
              type: MODAL_BUTTON_TYPE.DEFAULT,
              label: '다음에 하기',
              onClick: historyBack,
            },
            {
              type: MODAL_BUTTON_TYPE.PRIMARY,
              label: '계속하기',
              onClick: closeModal,
            },
          ],
        },
      });
      return;
    }
    historyBack();
  };

  const onClickWriteReview = (answerPid: string) => {
    moveToPage({
      webLink: `${ROUTES.QNA.WRITE_ANSWER_REVIEW.path(questionPid, answerPid)}`,
      mobileScreenName: ROUTES.QNA.WRITE_ANSWER_REVIEW.appScreenName,
      params: {
        questionPid: questionPid,
        answerPid: answerPid,
      },
    });
  };

  const onClickOuterLink = (url: string) => {
    if (isWebView()) {
      navigate.navigateToPureWebViewAppScreen({
        uri: url,
      });
    }
  };

  const extractTextFromChildren = (children: React.ReactNode): string => {
    if (typeof children === 'string') {
      return children;
    }
    if (typeof children === 'number') {
      return String(children);
    }
    if (Array.isArray(children)) {
      return children.map(extractTextFromChildren).join('');
    }
    if (React.isValidElement(children) && children.props && children.props.children) {
      return extractTextFromChildren(children.props.children);
    }
    return '';
  };

  const isEnabledWebDiagnosisCategory = targetQuestionCategory.isRecommendedTreatment && !client.isWebView;

  useEffect(() => {
    setQnaAttachments(qnaDetail?.attachments ?? []);
  }, [qnaDetail]);

  useEffect(() => {
    const images = qnaDetail?.isMine
      ? qnaAttachments
      : qnaAttachments.filter((attachment) => attachment.blindType !== AttachmentBlindType.Blinded);
    setSlideImages(images);
  }, [qnaDetail, qnaAttachments]);

  useEffect(() => {
    if (isNotMine && isPrivateQuestion) {
      if (isWebView()) {
        sendPostMessageToNative(NATIVE_POST_MESSAGE_TYPE.STACK_POP_TO_POP);
      } else {
        historyBack();
      }
      alert('비공개 질문입니다.');
    }
  }, [isPrivateQuestion, isNotMine]);

  useEffect(() => {
    if (!missionId || !missionCompleted) {
      return;
    }

    completeMissionMutation.mutate(
      {
        request: { missionId: missionId },
      },
      {
        onSuccess: () => {
          broadCastChannel.postMessage(UserMissionTypeVo.QnaRead);
        },
      }
    );
  }, [missionCompleted, missionId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!isFocused) {
      return;
    }
    refetchQnaDetail();
  }, [isFocused]);

  return {
    bannerAdExchange,
    handleSendExposureLog,
    onClickTopCta,
    qnaDetail,
    isQnaDetailLoading,
    relatedQna,
    isRelatedQnaLoading,
    qnaAttachments,
    imageZoomIndex,
    pageName,
    onShareModal,
    user,
    showReportModal,
    reportItems: reportItems ?? [],
    imageSlideIdx,
    isNotMine: isNotMine,
    isPrivateQuestion: isPrivateQuestion,
    timerVisible: Boolean(missionId),
    displayTime: displayTime,
    missionCompleted: missionCompleted,
    missionTargetTime: MISSION_TARGET_TIME,
    questionBannerInfo,
    isShowPrivateModal,
    isMyQuestion,
    onShowModal,
    onVisibleDimmedImage,
    onInValidateDetailQuery,
    onClickImage,
    onCloseImageZoom,
    onClickLikeQuestion,
    onChangeShowShareModal,
    onClickLikeAnswer,
    onClickMagazine,
    moveToDoctorDetailPage,
    onClickRelatedQna,
    onCloseReportModal,
    onChangeImageBlindType,
    onChangeSlideImage,
    onClickNearQna,
    moveToHospitalDetail,
    goBack,
    onClickWriteReview,
    onClickOuterLink,
    getStickyButtonText,
    shouldShowEventText,
    onClickStickButton,
    onClickBanner,
    toggleIsShowPrivateModal,
    onClickQuestionPrivate,
    targetQuestionCategory,
    subCategoryName,
    extractTextFromChildren,
    isEnabledWebDiagnosisCategory,
    hasSelectedRelatedQnaPid: !!selectedRelatedQna,
    onNavigateToTelemedicine,
    onNavigateToNextQna,
    onCloseRelatedQnaBottomSheet,
  };
};

export default useQuestionDetailViewModel;
